import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCheck, faTimes, faBan, faUserPlus, faSearch, faUserMinus, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import SendMail from './SendMail';
import Toast from '../../Shared/Toast';
import { useSocket } from '../../../contexts/SocketContext';
import styles from './FriendsList.module.css';

/* ── Types ──────────────────────────────────────────────── */

interface FriendMercenary {
  id: number;
  userId?: number;
  name: string;
  level: number;
  reputation: number;
  avatar: number;
  race: string;
  sex: string;
}

interface Friend {
  id: number;           // userId of the friend
  name: string;         // display name
  isOnline: boolean;
  lastSeen?: string;
  mercenary: FriendMercenary;
}

interface PendingFriend {
  id: number;           // userId
  name: string;
  mercenary: FriendMercenary;
  type: 'incoming' | 'outgoing';
}

interface SearchResult {
  mercenaryId: number;
  userId: number;
  mercenaryName: string;
  displayName: string;
  level: number;
  race: string;
  sex: string;
  avatar: number;
  reputation: number;
}

/* ── Component ──────────────────────────────────────────── */

const FriendsList: React.FC = () => {
  const { socket, onlineFriendIds } = useSocket();

  const [friends, setFriends] = useState<Friend[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [pendingFriends, setPendingFriends] = useState<PendingFriend[]>([]);

  // Local online-set seeded from API + updated by socket events
  const [onlineSet, setOnlineSet] = useState<Set<number>>(new Set());

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchSent, setSearchSent] = useState<Set<number>>(new Set());
  const searchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // Inline remove confirmation
  const [confirmRemoveId, setConfirmRemoveId] = useState<number | null>(null);

  // Pending section collapsed by default
  const [pendingOpen, setPendingOpen] = useState(false);

  // Mail modal
  const [showSendMail, setShowSendMail] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState<Friend | null>(null);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const friendsPerPage = 20;

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  /* ── Data fetching ────────────────────────────────────── */

  const fetchFriends = useCallback(async (page: number) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/friend/list', {
        params: { page, limit: friendsPerPage },
        withCredentials: true,
      });
      const data: Friend[] = response.data.friends;
      setFriends(data);
      setTotalPages(response.data.totalPages);
      // Seed local online set from API
      setOnlineSet(new Set(data.filter(f => f.isOnline).map(f => f.id)));
    } catch {
      displayToast('Failed to load friends list', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingFriends = useCallback(async () => {
    try {
      const response = await axios.get('/api/friend/pending', { withCredentials: true });
      setPendingFriends(response.data.pendingFriends);
    } catch {
      displayToast('Failed to load pending requests', 'error');
    }
  }, []);

  useEffect(() => {
    fetchFriends(currentPage);
  }, [currentPage, fetchFriends]);

  useEffect(() => {
    fetchPendingFriends();
  }, [fetchPendingFriends]);

  /* ── Socket listeners ─────────────────────────────────── */

  useEffect(() => {
    if (!socket) return;

    const handleOnline = (data: { userId: number }) => {
      if (data?.userId) setOnlineSet(prev => { const s = new Set(prev); s.add(data.userId); return s; });
    };
    const handleOffline = (data: { userId: number }) => {
      if (data?.userId) setOnlineSet(prev => { const s = new Set(prev); s.delete(data.userId); return s; });
    };
    const handleRequestHandled = () => {
      fetchPendingFriends();
      fetchFriends(currentPage);
    };

    socket.on('friendOnline', handleOnline);
    socket.on('friendOffline', handleOffline);
    socket.on('friendRequestHandled', handleRequestHandled);
    socket.on('newFriendRequest', fetchPendingFriends);

    return () => {
      socket.off('friendOnline', handleOnline);
      socket.off('friendOffline', handleOffline);
      socket.off('friendRequestHandled', handleRequestHandled);
      socket.off('newFriendRequest', fetchPendingFriends);
    };
  }, [socket, fetchPendingFriends, fetchFriends, currentPage]);

  /* ── Search (debounced) ───────────────────────────────── */

  useEffect(() => {
    if (searchRef.current) clearTimeout(searchRef.current);
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get('/api/friend/search', {
          params: { q: searchQuery.trim() },
          withCredentials: true,
        });
        setSearchResults(res.data.results);
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => { if (searchRef.current) clearTimeout(searchRef.current); };
  }, [searchQuery]);

  const handleSendRequest = async (targetUserId: number) => {
    try {
      await axios.post('/api/friend/request', { friendId: targetUserId }, { withCredentials: true });
      setSearchSent(prev => new Set(prev).add(targetUserId));
      displayToast('Friend request sent!', 'success');
    } catch (err: any) {
      displayToast(err?.response?.data?.message || 'Failed to send request', 'error');
    }
  };

  /* ── Actions ──────────────────────────────────────────── */

  const handleAccept = async (friendId: number) => {
    try {
      await axios.post('/api/friend/accept', { friendId }, { withCredentials: true });
      setPendingFriends(prev => prev.filter(f => f.id !== friendId));
      fetchFriends(currentPage);
      displayToast('Friend request accepted', 'success');
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to accept request', 'error');
    }
  };

  const handleDecline = async (friendId: number) => {
    try {
      await axios.post('/api/friend/decline', { friendId }, { withCredentials: true });
      setPendingFriends(prev => prev.filter(f => f.id !== friendId));
      displayToast('Friend request declined', 'success');
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to decline request', 'error');
    }
  };

  const handleCancel = async (friendId: number) => {
    try {
      await axios.post('/api/friend/cancel', { friendId }, { withCredentials: true });
      setPendingFriends(prev => prev.filter(f => f.id !== friendId));
      displayToast('Request cancelled', 'success');
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to cancel request', 'error');
    }
  };

  const handleRemove = async (friendId: number) => {
    try {
      await axios.post('/api/friend/remove', { friendId }, { withCredentials: true });
      setFriends(prev => {
        const next = prev.filter(f => f.id !== friendId);
        if (next.length === 0 && currentPage > 1) setCurrentPage(p => p - 1);
        return next;
      });
      setConfirmRemoveId(null);
      displayToast('Friend removed', 'success');
    } catch (err: any) {
      displayToast(err.response?.data?.message || 'Failed to remove friend', 'error');
    }
  };

  /* ── Derived data ─────────────────────────────────────── */

  const incomingRequests = pendingFriends.filter(f => f.type === 'incoming');
  const outgoingRequests = pendingFriends.filter(f => f.type === 'outgoing');

  // Merge API isOnline (in onlineSet) with live socket-context set
  const isLiveOnline = (id: number) => onlineSet.has(id) || onlineFriendIds.has(id);
  const onlineFriendList = friends.filter(f => isLiveOnline(f.id));
  const offlineFriendList = friends.filter(f => !isLiveOnline(f.id));

  const avatarSrc = (race: string, sex: string, avatar: number) =>
    `/img/avatars/${race?.toLowerCase() || 'default'}.${sex?.toLowerCase() || 'default'}.${avatar ?? 1}.png`;

  /* ── Render ───────────────────────────────────────────── */

  return (
    <div className={styles.container}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} duration={3000} onClose={() => setToastMessage(null)} />
      )}

      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>Friends List</h1>
        <div className={styles.headerStats}>
          <span className={styles.statPill}>
            <span className={styles.statOnlineDot} />
            {onlineFriendList.length} Online
          </span>
          <span className={styles.statPill}>{friends.length} Friends</span>
          {incomingRequests.length > 0 && (
            <span className={`${styles.statPill} ${styles.statPillPending}`}>
              {incomingRequests.length} Incoming
            </span>
          )}
        </div>
      </div>

      {/* Search bar */}
      <div className={styles.searchSection}>
        <div className={styles.searchWrapper}>
          <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search mercenaries to add as friends..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 200)}
          />
          {searchLoading && <span className={styles.searchSpinner} />}
        </div>

        {searchOpen && (searchResults.length > 0 ? (
          <div className={styles.searchDropdown}>
            {searchResults.map(r => (
              <div key={r.mercenaryId} className={styles.searchResult}>
                <img
                  src={avatarSrc(r.race, r.sex, r.avatar)}
                  alt={r.mercenaryName}
                  className={styles.searchAvatar}
                  onError={e => { (e.target as HTMLImageElement).src = '/img/no_img.png'; }}
                />
                <div className={styles.searchInfo}>
                  <span className={styles.searchMercName}>{r.mercenaryName}</span>
                  <span className={styles.searchMeta}>Lvl {r.level} &middot; {r.race}</span>
                </div>
                {searchSent.has(r.userId) ? (
                  <span className={styles.requestSentBadge}>Sent</span>
                ) : (
                  <button
                    className={styles.addButton}
                    onClick={() => handleSendRequest(r.userId)}
                  >
                    <FontAwesomeIcon icon={faUserPlus} /> Add
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : !searchLoading && searchQuery.trim().length >= 2 ? (
          <div className={styles.searchDropdown}>
            <div className={styles.searchEmpty}>No mercenaries found</div>
          </div>
        ) : null)}
      </div>

      {/* Pending Requests */}
      {pendingFriends.length > 0 && (
        <div className={styles.pendingSection}>
          <button className={styles.pendingToggle} onClick={() => setPendingOpen(o => !o)}>
            <span className={styles.sectionTitle}>Pending Requests</span>
            <span className={styles.sectionCount}>{pendingFriends.length}</span>
            <FontAwesomeIcon icon={pendingOpen ? faChevronUp : faChevronDown} className={styles.toggleChevron} />
          </button>
          {pendingOpen && <div className={styles.pendingInner}>
            {incomingRequests.length > 0 && (
              <>
                <p className={styles.pendingSubLabel}>Incoming</p>
                {incomingRequests.map(f => (
                  <div key={f.id} className={`${styles.friendRow} ${styles.pendingRow}`}>
                    <div className={styles.avatarWrapper}>
                      <img
                        src={avatarSrc(f.mercenary.race, f.mercenary.sex, f.mercenary.avatar)}
                        alt={f.mercenary.name || f.name}
                        className={styles.avatar}
                        onError={e => { (e.target as HTMLImageElement).src = '/img/no_img.png'; }}
                      />
                      <span className={styles.incomingDot} />
                    </div>
                    <div className={styles.friendInfo}>
                      <Link to={`/mercenary/${f.mercenary.id}`} className={styles.friendName}>
                        {f.mercenary.name || f.name}
                      </Link>
                      <div className={styles.friendMeta}>
                        <span>Lvl {f.mercenary.level}</span>
                        <span>{f.mercenary.race}</span>
                      </div>
                    </div>
                    <div className={styles.actions}>
                      <button className={styles.acceptBtn} onClick={() => handleAccept(f.id)}>
                        <FontAwesomeIcon icon={faCheck} /> Accept
                      </button>
                      <button className={styles.declineBtn} onClick={() => handleDecline(f.id)}>
                        <FontAwesomeIcon icon={faTimes} /> Decline
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {outgoingRequests.length > 0 && (
              <>
                <p className={styles.pendingSubLabel}>Outgoing</p>
                {outgoingRequests.map(f => (
                  <div key={f.id} className={`${styles.friendRow} ${styles.pendingRow}`}>
                    <div className={styles.avatarWrapper}>
                      <img
                        src={avatarSrc(f.mercenary.race, f.mercenary.sex, f.mercenary.avatar)}
                        alt={f.mercenary.name || f.name}
                        className={styles.avatar}
                        onError={e => { (e.target as HTMLImageElement).src = '/img/no_img.png'; }}
                      />
                    </div>
                    <div className={styles.friendInfo}>
                      <Link to={`/mercenary/${f.mercenary.id}`} className={styles.friendName}>
                        {f.mercenary.name || f.name}
                      </Link>
                      <div className={styles.friendMeta}>
                        <span>Awaiting response</span>
                      </div>
                    </div>
                    <div className={styles.actions}>
                      <button className={styles.cancelBtn} onClick={() => handleCancel(f.id)}>
                        <FontAwesomeIcon icon={faBan} /> Cancel
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          }
        </div>
      )}

      {/* Friends list */}
      {loading ? (
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
        </div>
      ) : friends.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>No friends yet</p>
          <p className={styles.emptyText}>Search for a mercenary above to send your first request.</p>
        </div>
      ) : (
        <>
          {onlineFriendList.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionOnlineDot} />
                <span className={styles.sectionTitle}>Online</span>
                <span className={styles.sectionCount}>{onlineFriendList.length}</span>
              </div>
              {onlineFriendList.map(f => renderFriendRow(f, true))}
            </div>
          )}

          {offlineFriendList.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>Offline</span>
                <span className={styles.sectionCount}>{offlineFriendList.length}</span>
              </div>
              {offlineFriendList.map(f => renderFriendRow(f, false))}
            </div>
          )}

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className={styles.pageLabel}>Page {currentPage} of {totalPages}</span>
              <button
                className={styles.pageBtn}
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showSendMail && selectedFriend && (
        <SendMail
          recipientId={selectedFriend.id}
          recipientName={selectedFriend.mercenary.name}
          onClose={() => { setShowSendMail(false); setSelectedFriend(null); }}
        />
      )}
    </div>
  );

  function renderFriendRow(f: Friend, isOnline: boolean) {
    const isConfirmingRemove = confirmRemoveId === f.id;

    return (
      <div
        key={f.id}
        className={`${styles.friendRow} ${isOnline ? styles.friendRowOnline : ''}`}
      >
        <div className={styles.avatarWrapper}>
          <img
            src={avatarSrc(f.mercenary.race, f.mercenary.sex, f.mercenary.avatar)}
            alt={f.mercenary.name}
            className={styles.avatar}
            onError={e => { (e.target as HTMLImageElement).src = '/img/no_img.png'; }}
          />
          <span className={isOnline ? styles.onlineDot : styles.offlineDot} />
        </div>

        <div className={styles.friendInfo}>
          <Link to={`/mercenary/${f.mercenary.id}`} className={styles.friendName}>
            {f.mercenary.name || f.name}
          </Link>
          <div className={styles.friendMeta}>
            <span>Lvl {f.mercenary.level}</span>
            <span>{f.mercenary.race}</span>
          </div>
        </div>

        <div className={styles.statusText}>
          {isOnline ? (
            <span className={styles.onlineLabel}>Online</span>
          ) : (
            <span
              className={styles.offlineLabel}
              title={f.lastSeen ? `Last seen ${new Date(f.lastSeen).toLocaleString()}` : undefined}
            >
              Offline
            </span>
          )}
        </div>

        <div className={styles.actions}>
          {isConfirmingRemove ? (
            <div className={styles.confirmRow}>
              <span className={styles.confirmText}>Remove?</span>
              <button className={styles.confirmYes} onClick={() => handleRemove(f.id)}>Yes</button>
              <button className={styles.confirmNo} onClick={() => setConfirmRemoveId(null)}>No</button>
            </div>
          ) : (
            <>
              <button
                className={styles.mailBtn}
                title="Send Mail"
                onClick={() => { setSelectedFriend(f); setShowSendMail(true); }}
              >
                <FontAwesomeIcon icon={faEnvelope} />
              </button>
              <button
                className={styles.removeBtn}
                title="Remove Friend"
                onClick={() => setConfirmRemoveId(f.id)}
              >
                <FontAwesomeIcon icon={faUserMinus} />
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
};

export default FriendsList;
